import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import type AudienceExportActivitiesTable from './index.js';
import type {ExportActivitySummary} from '@gl/lit/types/audience_exports.js';
import {isHTMLElement} from '@gl/lit/utils/dom.js';

const SCROLL_MARGIN_FOR_CLASS_CHANGE = 5;
const DEFAULT_PAGE_LIMIT = 12;
const DEFAULT_TABLE_DATA: ExportActivitySummary[] = [];
const DEFAULT_FILTERED_DATA: ExportActivitySummary[] = [];
const DEFAULT_PAGE = 0;
const DEFAULT_QUERY = '';

export type Model = {
  loading: boolean;
  tableData: ExportActivitySummary[];
  filteredData: ExportActivitySummary[];
  page: number;
  pageLimit: number;
  horizontallyScrolled: boolean;
  query: string;
  export_states: string[];
  destinations: string[];
  audiences: string[];
  filters: FilterStore;
};

export const Msg = makeVariants({
  setSearchQuery: Variant<string>()('set-search-query'),
  setExportStateFilter: Variant<string>()('set-export-state-filter'),
  setAudienceFilter: Variant<string>()('set-audience-filter'),
  setDestinationFilter: Variant<string>()('set-destination-filter'),
  setFilteredData: Variant<ExportActivitySummary[]>()('set-filtered-data'),
  setTableData: Variant<ExportActivitySummary[]>()('set-table-data'),
  setDataForFilters: Variant<void>()('set-data-for-filters'),
  goToNextPage: Variant<void>()('go-to-next-page'),
  goToPrevPage: Variant<void>()('go-to-prev-page'),
  setPage: Variant<number>()('set-page'),
  setLoading: Variant<boolean>()('set-loading'),
  scrollTable: Variant<CustomEvent>()('scroll-table'),
  resetFilters: Variant<void>()('reset-filters'),
});

export type Msg = VariantsFor<typeof Msg>;

type FilterStore = {
  export_state?: string;
  destination?: string;
  audience?: string;
};

function paginator(direction: 'prev' | 'next') {
  return function (host: AudienceExportActivitiesTable) {
    const target =
      direction === 'next' ? host.model.page + 1 : host.model.page - 1;

    const page =
      direction === 'next'
        ? host.totalPages > target
          ? target
          : host.totalPages
        : 0 < target
          ? target
          : 0;

    host.handleUpdate(Msg.setPage(page));
  };
}

// -- Init

function init(props: InitialProperties): ImmutableResult<Model> {
  const tableData = DEFAULT_TABLE_DATA;
  const page = <number>props.get('page') || DEFAULT_PAGE;
  const pageLimit = <number>props.get('pageLimit') || DEFAULT_PAGE_LIMIT;
  const filteredData =
    <ExportActivitySummary[]>props.get('filteredData') || DEFAULT_FILTERED_DATA;
  const query = <string>props.get('query') || DEFAULT_QUERY;

  return {
    loading: true,
    tableData: tableData,
    page: page,
    pageLimit: pageLimit,
    filteredData: filteredData,
    query: query,
    horizontallyScrolled: false,
    filters: {},
    export_states: getStates(tableData),
    destinations: getDestinations(tableData),
    audiences: getAudiences(tableData),
  };
}

// -- Update

function update(
  this: AudienceExportActivitiesTable,
  msg: Msg,
  model: Model,
  cmd: Cmd
): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'go-to-next-page':
      return [model, cmd(paginator('next'))] as const;
    case 'go-to-prev-page':
      return [model, cmd(paginator('prev'))] as const;
    case 'set-page':
      return {...model, page: msg.value} as const;
    case 'set-loading':
      return {...model, loading: msg.value} as const;
    case 'set-search-query':
      return {...model, query: msg.value} as const;
    case 'set-export-state-filter': {
      const newFilters = model.filters;
      newFilters.export_state = msg.value;
      return {...model, filters: newFilters} as const;
    }
    case 'set-destination-filter': {
      const newFilters = model.filters;
      newFilters.destination = msg.value;
      return {...model, filters: newFilters} as const;
    }
    case 'set-audience-filter': {
      const newFilters = model.filters;
      newFilters.audience = msg.value;
      return {...model, filters: newFilters} as const;
    }
    case 'set-filtered-data': {
      return [
        {
          ...model,
          filteredData: msg.value,
        },
        cmd(host => host.handleUpdate(Msg.setPage(1))),
      ];
    }
    case 'set-data-for-filters': {
      const export_states = getStates(this.model.tableData);
      const destinations = getDestinations(this.model.tableData);
      const audiences = getAudiences(this.model.tableData);
      return {
        ...model,
        export_states: export_states,
        destinations: destinations,
        audiences: audiences,
      } as const;
    }
    case 'set-table-data':
      return {...model, tableData: msg.value} as const;
    case 'reset-filters':
      return {...model, filters: {}} as const;
    case 'scroll-table': {
      const $el = msg.value.currentTarget;
      let horizontallyScrolled = false;
      if (isHTMLElement($el)) {
        const horizontalScroll = $el.scrollLeft;

        horizontallyScrolled =
          horizontalScroll > SCROLL_MARGIN_FOR_CLASS_CHANGE;
      }
      return {
        ...model,
        horizontallyScrolled: horizontallyScrolled,
      };
    }
  }
}

function getStates(tableData: ExportActivitySummary[]) {
  return [
    ...new Map(
      tableData.map(v => [v.export_state.state, v.export_state.state])
    ).values(),
  ];
}

function getDestinations(tableData: ExportActivitySummary[]) {
  return [
    ...new Map(
      tableData.map(v => [v.destination_name, v.destination_name])
    ).values(),
  ];
}

function getAudiences(tableData: ExportActivitySummary[]) {
  return [
    ...new Map(tableData.map(v => [v.audience_name, v.audience_name])).values(),
  ];
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  extendsAdapter: {
    page: {
      resolver: model => model.page,
      variant: Msg.setPage,
    },
    query: {
      resolver: model => model.query,
      variant: Msg.setSearchQuery,
    },
    filteredData: {
      resolver: model => model.filteredData,
      variant: Msg.setFilteredData,
    },
    tableData: {
      resolver: model => model.tableData,
      variant: Msg.setTableData,
    },
  },
};
